//
// Copyright (c) 2017 by Xolas.io. All Rights Reserved.
//

/*
Helper methods when posting stuff
----------------------------------------------------------------------- */
import axios from '@/lib/axiosClient'
import { promptReload } from '@/lib/requestRowSync'
import { isStepCompleted, hasListeners, reportStepCompleted, scopeOf, fallbackNotice } from '@/lib/stepCompleted'




export default {
  methods: {


    /**
     @param {String} url - the GET url.
     @param {Function} callback = null - if present will be called in success
     @param {String} text = 'information' - for messages.
     @param {Function} errorCallback = null - if present, will be called on failure of save.

     Calls fetchDataRawURL after wrapping getEndpoint(url)
     */
    async fetchData (url, callback = null, text = 'information', errorCallback = null) {
      return this.fetchDataRawURL(this.getEndpoint(url), callback, text, errorCallback)
    },



    /**
     @param {String} url - the GET raw url.
     @param {Function} callback = null - if present will be called in success
     @param {String} text = 'information' - for messages.
     @param {Function} errorCallback = null - if present, will be called on failure of save.
     */
    async fetchDataRawURL (url, callback = null, text = 'information', errorCallback = null) {

      axios.get(url)
        .then(response => {

          let data = response.data

          if (!this.checkWarning(response)) {
            if (response.data && response.data.data) {
              data = response.data.data
            }
          }

          if (this.isFunction(callback)) {
            callback(data)
          }

          return data

        })
        .catch(err => {
          // KAN-39: 401 is handled centrally in src/lib/axiosClient.js
          // (silent refresh + retry; redirect on terminal failure).
          console.error('fetchDataRawURL failed [', text, ']', url, 'message:', err)

          if (this.isFunction(errorCallback)) {
            errorCallback(err)
          }

          this.$notify(
            {
              title: 'Unable to fetch ' + text,
              message: this.describeFetchError(err),
              type: 'error',
              duration: 3000
            })
        })
    },


    /**
     XOL-440: a gate rejection is not a connectivity problem, but this handler
     rendered every failure as the raw axios Error ("Request failed with status
     code 400"), so a `tenant`/`role`/`route_acl` rejection reached the user as
     a network complaint. The API's gates all reply with a
     {error, reason, message} envelope alongside an X-Auth-Failure header —
     prefer the server's own wording when it sends one.

     @param {Error} err - the axios error.
     @returns {String|Error} a human-readable message, or the original error.
     */
    describeFetchError (err) {

      const body = err && err.response && err.response.data

      if (body && typeof body === 'object') {
        if (body.message) {
          return body.message
        }
        if (body.error) {
          return body.reason ? body.error + ': ' + body.reason : body.error
        }
      }

      return err
    },




    /**
     XOL-373 — fold the audit stamp out of a write response into the object the
     screen renders.

     The API stamps `updated_by` with the AUTHENTICATED contact (it no longer
     trusts the `updated_by` this mixin injects into `formData` below) and
     returns the saved record. Any screen showing "Last update: <date> by <name>"
     must therefore re-read both keys from the response, or it keeps displaying
     the PREVIOUS editor and the previous date until something refetches —
     which reads as though the edit never happened, while crediting someone else.

     It lives here, rather than in each component, because that is exactly how
     the gap arose: the same few lines were inlined into some success callbacks
     and absent from others, and nothing made the omission visible. Callers pass
     their own target because the object differs per screen (`details`, a row, a
     prop-shared licensee).

     A response that omits the keys leaves the existing values ALONE rather than
     blanking them — mirroring the API's own rule that a stale attribution is bad
     but an erased one is worse. `$set` because a target that never carried the
     keys would otherwise take a non-reactive assignment.

     @param {Object} target - the rendered object to update (e.g. `this.details`).
     @param {Object} data - the write response body.
     */
    applyAuditStamp (target, data) {
      if (!target || !data) return

      if (data.updated_at !== undefined) {
        this.$set(target, 'updated_at', data.updated_at)
      }

      if (data.updated_by !== undefined) {
        this.$set(target, 'updated_by', data.updated_by)
      }
    },

    /**
     @param {this} self - for messages and alerts.
     @param {Number} id - if null will create, else save.
     @param {String} url - the POST/PUT url.
     @param {Object} formData - data to be saved.
     @param {String} item for messages, etc.
     @param {Function} successCallback - if present will be called in success
     @param {Function} failCallback - if present, will be called on failure of save.
     @param {Boolean} showMessage = true, if false then no messages will be shown.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.
     @param {Boolean} notifyFailureCallback = false - forwarded to send() verbatim; see send()'s
       docblock for exactly which failure branches this opts failCallback into.
     @param {Boolean} allowViewOnly = false - forwarded to send() verbatim; bypasses the view-only
       write-block for self-service surfaces (e.g. own profile/password) that the API also exempts.

     Original saveForm, the tableRef param is a later addition, not break existing code.
     */
    async saveForm (self, url, id, formData, item = 'item', successCallback, failCallback, showMessage = true, tableRef = null, notifyFailureCallback = false, allowViewOnly = false) {

      if (!formData.updated_by) {
        formData.updated_by = this.userName()
      }

      if (id === null) {
        formData.created_by = this.userName()
      }

      var formAction = null

      // If has a valid ID for the object, the update, else add.
      if (id) {
        formAction = axios.put
        url = this.getEndpoint(url, id)
      } else {
        formAction = axios.post
        url = this.getEndpoint(url)
      }

      return this.send(self, url, formData, item, successCallback, failCallback, formAction, showMessage, 'Save', tableRef, notifyFailureCallback, allowViewOnly)
    },



    /**
     The "saved" toast body `send()` raises when showMessage is on: '[<item>] updated'. One source so a
     caller that saves with showMessage = false and still wants the same confirmation (FileDetails,
     when an approval has no mail toast to show) cannot drift from it.
     */
    updatedMessage (item = 'item') {
      return '[' + item + '] updated'
    },

    /**
     @param {this} self - for messages and alerts.
     @param {String} url - the POST/PUT url.
     @param {Object} formData - data to be saved.
     @param {String} item for messages, etc.
     @param {Function} successCallback - if present will be called in success
     @param {Function} failCallback - if present, will be called on failure of save.
     @param {axios} formAction - put, post, delete, etc.
     @param {Boolean} showMessage = true, if false then no messages will be shown.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.
     @param {Boolean} notifyFailureCallback = false - opt-in: when true, failCallback is also invoked
       on 418, 403, data.error, data.warning, the generic (has-response) fallback, and the no-response
       network-error/timeout/CORS branch (in addition to the always-on data.errors branch). Default
       false preserves original behaviour for all existing callers.

     @param {Boolean} allowViewOnly = false - see saveForm.
     @param {Boolean} quiet = false - XOL-596: skip every toast send() would raise itself (view-only,
       403, 409, 422, data.error, network, ...). failCallback is still called, with the axios error, on
       every failure branch the caller opted into with notifyFailureCallback; the caller reports instead
       (the field-save notice batcher in lib/requestRowSync). Other callers are unchanged.

     @return {Promise} XOL-596: resolves once the request AND its callbacks have run (undefined when the
       view-only guard returns early). A failed request is handled inside, so the promise does not
       reject for it; it can still reject if `formAction` itself throws synchronously. The two
       callbacks differ: an exception thrown by `successCallback` is caught by the chain's own
       `.catch` and surfaces as a "Connection problem" (the pre-existing behaviour), but the
       failure callbacks run INSIDE that `.catch`, so an exception thrown by `failCallback`
       REJECTS the returned promise. A caller that must always settle (the field-save sender,
       the command runner) handles that rejection. Callers that need the outcome capture it in
       their callbacks, and must not rely on the resolved value.

     XOL-595 contract v3 error bodies are {code, message, error}, where `error` repeats the sentence.
     The 428 / 409 / 422 branches key on HTTP status plus `code`, never on `error`. A 422 without a
     `code` is Laravel validation and still takes the `data.errors` branch.

     Lower level function, for calls in PostMixins only.
     */
    async send (self, url, formData, item = 'item', successCallback = null, failCallback = null, formAction = axios.post, showMessage = false, actionType = 'Post', tableRef = null, notifyFailureCallback = false, allowViewOnly = false, quiet = false) {

      // XOL-371: view-only users are blocked from all writes EXCEPT their own
      // self-service surfaces (profile self-edit, own password), which pass
      // allowViewOnly=true. The API enforces the same exemption server-side
      // (PUT /api/profile is exempt from the view-only write-block).
      if (this.isViewOnly() && !allowViewOnly) {
        if (!quiet) {
          self.$message({
            type: 'warning',
            title: 'View Only User',
            message: 'You are view only, can\'t save'
          })
        }

      // XOL-490/493: this used to be the only path in send() that returned
      // without invoking failCallback (see the removed XOL-487 comment this
      // replaces). Popup.vue:36 disables the trigger for view-only users, so
      // a keepOpenUntilSaved consumer never reaches this branch today — but
      // gating failCallback here the same way every sibling branch below
      // gates it means a future keepOpenUntilSaved consumer that doesn't
      // disable its own trigger no longer hangs "saving" forever.
      if (notifyFailureCallback && failCallback) { failCallback() }
      return

      }

      // XOL-596: every toast goes through here, so `quiet` silences them all.
      const notify = (options) => {
        if (!quiet) {
          self.$notify(options)
        }
      }

      if (formAction) {
        return formAction(url, formData)
        .then(response => {

          if (!this.checkWarning(response)) {

            let data = response.data

            if (response.data && response.data.length >= 1) {
              data = response.data[0]
            }

            this.eventTableRef(tableRef)

            if (successCallback && response && response.status === 200) {
              // XOL-596: a quiet caller (a per-field content save) leaves the
              // stats refresh to its own burst; one GET per field would be noise,
              // and a content edit changes no count.
              if (!quiet) {
                this.refreshStats()
              }
              successCallback(data)
            }

            if (showMessage) {
              self.$message({
                type: 'success',
                title: 'Success',
                message: this.updatedMessage(item)
              })
            }

            return data

          }

          return null
        })
        .catch(err => {


          if (err.response) {
            console.error(actionType, err)

            let data = err.response.data

            // KAN-39: 401 is handled centrally in src/lib/axiosClient.js
            // (silent refresh + retry; redirect on terminal failure).

            // XOL-596 (XOL-595 contract v3): 428 / 409 / 422 carry a machine
            // `code`. Key off status + code; `data.error` is a sentence and is
            // never read as a code. These sit before the 418 / data.error
            // branches, which would otherwise swallow them.
            const status = err.response.status
            const code = (data && typeof data === 'object' && typeof data.code === 'string') ? data.code : null

            // 428 version_required: this bundle sent no contentVersion, or is
            // out of date. No generic toast; the reload prompt owns it.
            if (status === 428 && code === 'version_required') {
              promptReload(self, { err: err, item: item, actionType: actionType })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            // 403 step_completed (XOL-596 completed steps): the step this write belongs
            // to was completed. NOT a permission problem, so no "You do not have
            // permission" toast. The failure callback is called whatever
            // `notifyFailureCallback` says (the caller must put its editor back and
            // report the refusal with `stepCompletedRefusal`), and the request drawer
            // says why in the C-12 card. A quiet caller (a field save, a command) has
            // its own handling. When no request drawer takes it (a site page's file
            // removal, say) the refusal is still said, in the step_completed wording
            // ("Step n has been completed, so your change wasn't saved"), never as a
            // permission problem.
            if (!quiet && isStepCompleted(err)) {
              if (failCallback) { failCallback(err) }

              if (!hasListeners(scopeOf(err, formData))) {
                notify(Object.assign({ type: 'warning', duration: 5500 }, fallbackNotice(err, this.canReopenForNotice())))
              }

              return
            }

            // 409 stale_field / stale_decision / request_closed / invalid_state.
            // A caller that opted into failCallback owns the message (it shows
            // SPA copy built from the adopted rows); others get one toast.
            if (status === 409 && code) {
              if (notifyFailureCallback && failCallback) {
                failCallback(err)
              } else {
                notify({
                  title: 'Not saved',
                  message: data.message || data.error,
                  type: 'warning',
                  duration: 5500
                })
              }
              return
            }

            // 422 read_only_fields / invalid path: a bug signal, not a user
            // error. `data.fields` lists the offending paths.
            if (status === 422 && code) {
              console.error(actionType + ' rejected [' + code + ']', data.fields, data.message)

              notify({
                title: 'Problem',
                message: data.message || data.error,
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            // Status: 418 I'm a teapot - Used by Telco errors
            if (data && data.status && data.status === 418) {
              notify({
                title: 'Problem',
                message: 'Unable to ' + actionType.toLowerCase() + ' ' + item + ' [' + err.response.data.error + ']',
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            // XOL-60: 403 Forbidden — backend role enforcement (see XOL-61)
            if (err.response.status === 403) {
              console.warn('[XOL-60] 403 on', actionType, item, '— user role likely mismatched for this action')
              notify({
                title: 'Not allowed',
                message: (data && (data.error || data.message))
                  ? 'You do not have permission to ' + actionType.toLowerCase() + ' ' + item + ' [' + (data.error || data.message) + ']'
                  : 'You do not have permission to ' + actionType.toLowerCase() + ' ' + item + '. Contact your administrator if you believe this is a mistake.',
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }

            if (data && data.error) {
              notify({
                title: 'Problem',
                message: data.error,
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }


            if (data && data.warning) {
              notify({
                title: 'Warning',
                message: data.warning,
                type: 'warning',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }
              return
            }


            if (data && data.errors) {

              let errors = data.errors
              let i = 0
              let len = 0


              // count props..
              for (const p in errors) {
                console.warn(`${p}`, errors[p][0])
                len++
              }


              for (const prop in errors) {

                notify({
                  title: 'Problems [' + (i + 1) + ' of ' + len + ']',
                  message: errors[prop][0],
                  type: 'warning',
                  duration: 5500
                })

                i++

              }  // for

              if (failCallback) {
                // XOL-596: a quiet caller classifies by the axios error.
                failCallback(quiet ? err : errors)
              }


            } else {

              notify({
                title: 'Problems',
                message: 'Unable to ' + actionType.toLowerCase() + ' ' + item + ' [' + err.status + '] ' + (err.response.data ? err.response.data : err.response.message),
                type: 'error',
                duration: 5500
              })

              if (notifyFailureCallback && failCallback) { failCallback(err) }

            }

          } else {

            // XOL-487: no err.response means a network error / timeout / CORS
            // failure — the request never reached the server, or its reply
            // never reached us. Previously this branch didn't exist, so a
            // keepOpenUntilSaved consumer (RatesAction) wired up here would
            // never learn the save had failed and its drawer would hang
            // "saving" forever. The toast is unconditional, but failCallback
            // is gated on notifyFailureCallback — same opt-in as every other
            // branch above. A keepOpenUntilSaved consumer MUST pass
            // notifyFailureCallback = true, or its drawer will hang on a
            // network error; RatesAction does.
            notify({
              title: 'Connection problem',
              message: 'Unable to reach the server. Your changes may not have been saved — check your connection and try again.',
              type: 'error',
              duration: 5500
            })

            if (notifyFailureCallback && failCallback) { failCallback(err) }

          } // if err.reponse

        })  // catch
      }
    },  // send




    /**
     @param {Object} err - the axios error of a failed write.
     @param {String} what - a key of requestSync.completed.what.* (what was refused).
     @param {Object} scope - optional { actionStepId, actionId }: which request it came from, so only
       that request's drawer reports it (several can be mounted).
     @return {Boolean} true when `err` is a 403 step_completed AND the request drawer took it
       (re-reads the request, then says why in the C-12 card). The caller's failure callback
       calls this; a false return means "not mine", and the caller does what it always did.
     */
    stepCompletedRefusal (err, what, scope) {
      return reportStepCompleted(err, what, scope)
    },

    // Can this user re-open a step? Only to word the fallback notice (the same gate as the
    // Complete Step box: a Tower Co manager who can write).
    canReopenForNotice () {
      return !!(this.isTowerCo && this.isTowerCo() && this.isManager && this.isManager() && !(this.isViewOnly && this.isViewOnly()))
    },

    /**
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.

     Fires refresh for each tableRef.
     */
    eventTableRef (tableRef) {

      let tr = '-refresh-table'

      if (tableRef) {
        if (this.isString(tableRef)) {
          this.$events.emit(tableRef + tr)

        } else if (this.isArray(tableRef)) {
          for (var i = 0; i < tableRef.length; i++) {
            this.$events.emit(tableRef[i] + tr)
          }
        }
      }

    },




    /**
     @param {String} sUrl - download URL
     @param {String} fileName = null, for download

     @return {Boolean} true if successful, else false

     The downloadFile code is from: https://github.com/PixelsCommander/Download-File-JS
     (C) Copyright 2013 Denis Radin. Apache License terms.
      Modified to ES6 standards.
      */
    downloadFile (sUrl, fileName = null) {

      // iOS devices do not support downloading. We have to inform user about this.
      if (/(iP)/g.test(navigator.userAgent)) {
        alert('Your device does not support files downloading. Please try again in desktop browser.')
        return false
      }

      // If in Chrome or Safari - download via virtual link click
      if (this.getStore().isChrome || this.getStore().isSafari) {
        // Creating new link node.
        var link = document.createElement('a')

        link.target = '_blank'
        link.href = sUrl

        if (link.download !== undefined) {
          // Set HTML5 download attribute. This will prevent file from opening if supported.
          fileName = fileName ? fileName : sUrl.substring(sUrl.lastIndexOf('/') + 1, sUrl.length)
          link.download = fileName
        }

        // Dispatching click event.
        if (document.createEvent) {
          var e = document.createEvent('MouseEvents')
          e.initEvent('click', true, true)
          link.dispatchEvent(e)
          return true
        }
      }

      // Force file download (whether supported by server).
      if (sUrl.indexOf('?') === -1) {
        sUrl += '?download'
      }

      window.open(sUrl, '_self')
      return true
    },



    /**
     @param {Object} data - file data to download.
     @param {String} fileName for messages, etc.
     @param {String} type default: 'application/octet-stream'

     download data via dynamic link.
     */
    download (data, filename, type = 'application/octet-stream') {
      var file = new Blob([data], {type: type})
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(file, filename)  // IE10+
      } else { // Others
        var a = document.createElement('a'),
          url = URL.createObjectURL(file)
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        setTimeout(function () {
          document.body.removeChild(a)
          window.URL.revokeObjectURL(url)
        }, 0)
      }
    },




    /**

     @param {this} self - for messages and alerts.
     @param {Number} id - the ID of the record to delete.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.
     @param {Object} extra = null - XOL-596: { body, what, failCallback }. `body` is sent as the DELETE's JSON
       body (`actionStepId`, the step context of a request-step UI). `what` names the refused action for a
       403 step_completed (a key of requestSync.completed.what.*), which `deleteRaw` then reports to the
       request drawer instead of raising its "Delete failed" toast; `failCallback` is called on any failure.

     If confirm = true pops up a confirm dialog with contents for 'message'
     */
    async deleteItem (self, id, url, message, callback, confirm = true, tableRef = null, confirmYesText = 'Yes', extra = null) {

      if (this.isViewOnly()) {
        self.$message({
          type: 'warning',
          title: 'View Only User',
          message: 'You are view only, can\'t delete'
        })

      return

      }


      url = this.getEndpoint(url, id)

      if (confirm) {
        self.$confirm('This will permanently delete <b>' + message + '</b> Continue?', 'Warning', {
          confirmButtonText: confirmYesText,
          cancelButtonText: 'No',
          center: true,
          dangerouslyUseHTMLString: true,
          type: 'error'
        }).then(() => {
          return this.deleteRaw(self, url, id, message, callback, true, tableRef, extra)
        }).catch(() => {
          self.$message({
            type: 'info',
            message: 'Delete cancelled'
          })
        })
      } else {
        return this.deleteRaw(self, url, id, message, callback, false, tableRef, extra)
      }

      return null
    },   // deleteItem



    /**

     @param {this} self - for messages and alerts.
     @param {Number} id - the ID of the record to delete.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.
     @param {String/Array} tableRef refreshes table, or multiple tables, if array of string.

     @param {Object} extra = null - see deleteItem.

     Without wrapping getEndpoint(url).
     */
    async deleteRaw (self, url, id, message, callback, showMsgs = true, tableRef = null, extra = null) {
      // The second argument of axios.delete is a CONFIG; the record id that used to be passed here
      // was ignored. With a step context the config carries the JSON body (`data`).
      axios.delete(url, extra && extra.body ? { data: extra.body } : id)
      .then(response => {
        if (!this.checkWarning(response, showMsgs)) {

          this.refreshStats()

          if (showMsgs) {
            self.$message({type: 'success', message: 'Delete [' + message + '] completed'})
          }

          let data = response.data[0]

          this.eventTableRef(tableRef)

          if (callback) {
            callback(data)
          }

          return data
        }

      })
      .catch(err => {
        // 403 step_completed (XOL-596): the request drawer says why; no toast of ours.
        if (extra && isStepCompleted(err)) {
          var scope = scopeOf(err, extra.body)

          if (extra.scope) { Object.assign(scope, extra.scope) }
          if (typeof extra.failCallback === 'function') { extra.failCallback(err) }

          if (!reportStepCompleted(err, extra.what, scope)) {
            // Nobody took it: said in the step_completed wording, not as "Delete failed".
            self.$notify(Object.assign({ type: 'warning', duration: 5500 }, fallbackNotice(err, this.canReopenForNotice())))
          }

          return
        }

        if (extra && typeof extra.failCallback === 'function') { extra.failCallback(err) }

        self.$notify({
          type: 'error',
          message: 'Delete failed ' + err.message
        })
      })  // catch
    },    // deleteRaw




    /**

     @param {this} self - for messages and alerts.
     @param {String} url - the POST/PUT url.
     @param {String} message - Message to display in the confirm dialog.
     @param {Function} callback - if present will be called with what ever the server responds with.

     Without ID, your URL must contain valid delete clauses.
     */
    async delete (self, url, message, callback) {
      url = this.getEndpoint(url)

      self.$confirm('This will permanently delete <b>' + message + '</b> Continue?', 'Warning', {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        center: true,
        dangerouslyUseHTMLString: true,
        type: 'warning'
      }).then(() => {

        axios.delete(url)
        .then(response => {

          if (callback) {
            callback(response)
          }

          if (!this.checkWarning(response)) {

            this.refreshStats()

            self.$message({
              type: 'success',
              message: 'Delete [' + message + '] completed'
            })

          }

          return response
        })
        .catch(err => {
          self.$notify({
            type: 'error',
            message: 'Delete failed ' + err.message
          })
        })  // catch
      }).catch(() => {
        self.$message({
          type: 'info',
          message: 'Delete cancelled'
        })
      })
    }   // delete

  }
}
